import { sortBy } from "lodash";
import moment from "moment";
import { ReactNode } from "react";
import {
  ComplianceProgramAppliesTo,
  ComplianceProgramRecurrence,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import { stepNames } from "@apps/programs/components/compliance-program-timeline-vocabulary";
import { formatNameList, getAppliesToLabel } from "@apps/programs/helpers";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";

import { stepOrder } from "../helpers";
import { ProgramForm } from "../types";
import { plural } from "./readiness-figures";

/** More names than this read as a list to scan, not a sentence. */
const maxNames = 3;

const formatDate = (date: string) => moment(date).format("D MMM YYYY");

const recurrenceText: Partial<Record<ComplianceProgramRecurrence, string>> = {
  [ComplianceProgramRecurrence.Monthly]: "Repeats monthly",
  [ComplianceProgramRecurrence.Quarterly]: "Repeats quarterly",
  [ComplianceProgramRecurrence.Yearly]: "Repeats yearly",
};

/** "Ana, Ben, Cleo and 4 more", so a long selection stays one line. */
const shortNameList = (names: string[]) =>
  names.length <= maxNames + 1
    ? formatNameList(names)
    : `${names.slice(0, maxNames).join(", ")} and ${names.length - maxNames} more`;

const Fact = ({ title, children }: { title: string; children: ReactNode }) => (
  <div className="flex gap-4">
    <dt className="w-24 shrink-0 text-gray-500">{title}</dt>
    <dd className="min-w-0 flex-1 text-gray-900">{children}</dd>
  </div>
);

/**
 * The review at a glance before it is published: its name, when it runs, which
 * steps it has and who takes part. Read from the form alone, with no query.
 */
const ProgramFormSummary = ({
  proposedComplianceProgram: program,
  creator,
}: {
  proposedComplianceProgram: ProgramForm;
  creator: BasicUser;
}) => {
  const label = useLabel();
  const names = stepNames(label);
  const steps = sortBy(program.steps, (step) => stepOrder[step.stepType]);

  // Zero is missing here, as it is to the Timeline and to publishing: the form
  // does not let a step open on the day somebody joins. A due date on or before
  // the kickoff is not one either.
  const kickoffAfter = program.startDateOffset;
  const dueAfterKickoff =
    program.startDateDueDateOffset && kickoffAfter
      ? program.startDateDueDateOffset - kickoffAfter
      : 0;
  const dates = program.ongoing
    ? !kickoffAfter
      ? "Conditional. Kickoff and due dates not set."
      : dueAfterKickoff <= 0
        ? `Conditional. Kicks off ${plural(kickoffAfter, "day")} after each person's start date. Due date not set.`
        : `Conditional. Kicks off ${plural(kickoffAfter, "day")} after each person's start date, due ${plural(dueAfterKickoff, "day")} later.`
    : program.startDate && program.dueDate
      ? `${formatDate(program.startDate)} – ${formatDate(program.dueDate)}`
      : "Kickoff and due dates not set.";
  const recurrence = program.ongoing
    ? undefined
    : recurrenceText[program.recurrence];

  const selection =
    program.appliesTo === ComplianceProgramAppliesTo.Departments
      ? program.appliesToTeams.map((team) => team.title)
      : program.appliesTo === ComplianceProgramAppliesTo.ReportsTo
        ? program.appliesToManagers.map((manager) => manager.name)
        : program.appliesTo === ComplianceProgramAppliesTo.Users
          ? program.appliesToUsers.map((user) => user.name)
          : [];
  const exclusions = [
    program.excludedUsers.length > 0 &&
      `leaves out ${shortNameList(program.excludedUsers.map((user) => user.name))}`,
    program.excludedHireDateAfter &&
      `leaves out people who start after ${formatDate(program.excludedHireDateAfter)}`,
  ].filter(Boolean);

  return (
    <div className="rounded-md border bg-white p-6 text-sm">
      <div className="tf-header-base-dark">{program.title || "Untitled"}</div>
      <dl className="mt-3 space-y-1.5">
        <Fact title="Dates">
          {recurrence ? `${dates} · ${recurrence}` : dates}
        </Fact>
        <Fact title="Steps">
          {steps.length > 0
            ? steps.map((step) => names[step.stepType]).join(" · ")
            : "No steps turned on yet."}
        </Fact>
        <Fact title="Participants">
          {getAppliesToLabel(
            label,
            program.appliesTo,
            creator,
            currentUserVar().id,
          )}
          {selection.length > 0 && `: ${shortNameList(selection)}`}
          {exclusions.length > 0 && (
            <span className="text-gray-500"> ({exclusions.join("; ")})</span>
          )}
        </Fact>
      </dl>
    </div>
  );
};

export default ProgramFormSummary;
